'use client';
import { BREAKPOINTS } from '@mnd-frontend/ui/shared/tokens';
import type { ColorPropType } from '@mnd-frontend/ui/shared/types/color';
import { isValidHex } from '@mnd-frontend/utils';
import type { HTMLAttributes, ReactNode } from 'react';
import styled from 'styled-components';

const H1 = styled.h1<{ $color?: ColorPropType; $textAlign?: TextAlign }>`
  margin: 0;
  /* web/h1/mobile */
  font-size: clamp(2.25rem, 8.5vw, 4.5rem);
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -0.1rem;
  color: ${({ $color }) => ($color ? `${$color}` : 'inherit')};
  white-space: pre-line; //allow line breaks in title

  @media (min-width:  ${BREAKPOINTS.XS}px) {
    font-size: clamp(3rem, 8.5vw, 4.5rem);
    letter-spacing: -0.225rem;
  }

  && {
    /* Text-align prop should always be first */
    text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : undefined)};
  }
`;

const H2 = styled.h2<{
  $color?: ColorPropType;
  $isSmallFont?: boolean;
  $textAlign?: TextAlign;
}>`
  margin: 0;
  /* web/h2/mobile */
  font-size: ${({ $isSmallFont }) => ($isSmallFont ? '1.5rem' : '2.25rem')};
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.0525rem;
  color: ${({ $color }) => ($color ? `${$color}` : 'inherit')};
  white-space: pre-line; //allow line breaks in title

  @media (min-width: ${BREAKPOINTS.SM}px) {
    font-size: ${({ $isSmallFont }) => ($isSmallFont ? '1.75rem' : '3rem')};
  }

  && {
    /* Text-align prop should always be first */
    text-align: ${({ $textAlign }) => ($textAlign ? $textAlign : undefined)};
  }
`;

type TagNameLiteral = 'h1' | 'h2';
export const headingLevels: Array<TagNameLiteral> = ['h1', 'h2'];

type HeadingTagName = TagNameLiteral;
type TextAlign = 'left' | 'center' | 'right';

const Heading = ({
  as,
  color,
  children,
  isSmallFont,
  textAlign,
  id,
}: {
  as: HeadingTagName;
  children: ReactNode;
  color?: ColorPropType | 'inherit';
  isSmallFont?: boolean;
  textAlign?: TextAlign;
  id?: string;
} & HTMLAttributes<HTMLHeadingElement>) => {
  if (color === 'inherit') return undefined;

  if (typeof color === 'string' && !isValidHex(color))
    throw new Error('Dev Error: Set a hex color to the color prop');

  if (as === 'h1') {
    return (
      <H1 {...(id ? { id } : undefined)} $color={color} $textAlign={textAlign}>
        {children}
      </H1>
    );
  }

  return (
    <H2
      {...(id ? { id } : undefined)}
      $color={color}
      $isSmallFont={isSmallFont}
      $textAlign={textAlign}
    >
      {children}
    </H2>
  );
};

export default Heading;
